import React, { createContext, useContext, useState, useEffect } from 'react';
import { useParams, useSearchParams, Outlet } from 'react-router-dom';
import { CompetitionMetadata, subscribeCompetition } from '../firebase/competitionService';

// ─── Context value ────────────────────────────────────────────────────────────

interface CompetitionContextValue {
  competitionId: string;
  metadata: CompetitionMetadata | null;
  /** True when the ?secret= query param matches the competition's adminSecret. */
  isAdmin: boolean;
  /** The raw secret value from the URL (needed to pass through to child links). */
  adminSecret: string;
}

const CompetitionContext = createContext<CompetitionContextValue | null>(null);

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useCompetition(): CompetitionContextValue {
  const ctx = useContext(CompetitionContext);
  if (!ctx) throw new Error('useCompetition must be used inside CompetitionLayout');
  return ctx;
}

// ─── CompetitionLayout ────────────────────────────────────────────────────────
// Used as a layout route in App.tsx for /competition/:competitionId/*

export const CompetitionLayout: React.FC = () => {
  const { competitionId = '' } = useParams<{ competitionId: string }>();
  const [searchParams] = useSearchParams();
  const adminSecret = searchParams.get('secret') ?? '';

  const [metadata, setMetadata] = useState<CompetitionMetadata | null>(null);

  useEffect(() => {
    if (!competitionId) return;
    return subscribeCompetition(competitionId, setMetadata);
  }, [competitionId]);

  const isAdmin =
    !!adminSecret &&
    !!metadata &&
    adminSecret === metadata.adminSecret;

  return (
    <CompetitionContext.Provider value={{ competitionId, metadata, isAdmin, adminSecret }}>
      <Outlet />
    </CompetitionContext.Provider>
  );
};

// ─── LegacyBK2026Provider ─────────────────────────────────────────────────────
// Wraps old hardcoded routes (/boulder-admin, /jury, etc.) so they keep working
// as bk2026 admin pages without needing a secret.

export const LegacyBK2026Provider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <CompetitionContext.Provider
    value={{ competitionId: 'bk2026', metadata: null, isAdmin: true, adminSecret: '' }}
  >
    {children}
  </CompetitionContext.Provider>
);

// ─── AdminAccessGate ──────────────────────────────────────────────────────────
// Renders children when isAdmin, otherwise shows a secret-entry form that
// redirects to the same URL with ?secret=XXX appended.

export const AdminAccessGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAdmin, competitionId, metadata } = useCompetition();
  const [, setSearchParams] = useSearchParams();
  const [input, setInput] = useState('');
  const [rejected, setRejected] = useState(false);

  if (isAdmin) return <>{children}</>;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (metadata && input === metadata.adminSecret) {
      setSearchParams({ secret: input });
    } else {
      setRejected(true);
      setInput('');
    }
  };

  return (
    <div className="min-h-screen bg-dark text-white flex items-center justify-center px-4">
      <div className="bg-gray-800 rounded-2xl p-8 max-w-sm w-full shadow-xl">
        <div className="text-4xl mb-4 text-center">🔒</div>
        <h1 className="text-xl font-bold text-center mb-1">Admin Access Required</h1>
        <p className="text-gray-400 text-sm text-center mb-6">
          {metadata?.name ?? competitionId} — enter the organiser secret to continue.
        </p>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <input
            type="password"
            value={input}
            onChange={e => { setInput(e.target.value); setRejected(false); }}
            placeholder="Admin secret"
            autoFocus
            className="px-4 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {rejected && (
            <p className="text-red-400 text-sm text-center">Incorrect secret. Try again.</p>
          )}
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold text-sm"
          >
            Enter
          </button>
        </form>
      </div>
    </div>
  );
};
